<template>
  <li
    class="
      tw-group
      tw-flex
      tw-flex-row
      tw-items-center
      tw-justify-between
      tw-select-none
      tw-text-sm
      tw-leading-4
      tw-transition-all
      tw-duration-300
      tw-min-w-0
    "
    :class="containerClasses"
    v-bind="modifiedAttrs"
    :data-testid="`sf-list-item-${props.itemId}`"
    @click="handleClick"
  >
    <div
      class="
        tw-flex
        tw-flex-auto
        tw-flex-row
        tw-items-center
        tw-justify-start
        tw-justify-self-start
        tw-min-w-0
      "
    >
      <a
        :href="props.href ?? '#'"
        :rel="props.href ? 'noopener' : undefined"
        :class="linkClasses"
        data-testid="sf-item-left"
        @click="handleLeftClick"
        @auxclick="handleLeftAuxClick"
      >
        <div
          class="
            tw-flex tw-items-center tw-justify-center tw-text-sm tw-leading-4
            empty:tw-hidden
          "
          :class="[iconMarginClass, props.iconExtraClasses]"
        >
          <slot name="icon" />
        </div>
        <span :class="[labelWrapperClass, props.contentExtraClasses]">
          <slot />
        </span>
      </a>
    </div>
    <div
      class="
        tw-flex tw-flex-row tw-items-center tw-justify-start tw-justify-self-end
      "
    >
      <a
        href="#"
        :class="rightLinkClasses"
        data-testid="sf-item-right"
        @click.prevent.stop="handleRightClick"
      >
        <slot name="right-content" />
      </a>
    </div>
  </li>
</template>

<script lang="ts" setup>
import { computed, useAttrs } from 'vue'

import { isNewTabClick } from '@app/utils/link-clicks'

import type { ItemVariants } from './constants'
import { itemVariant } from './constants'

defineOptions({
  name: 'SfListItem',
})

interface Props {
  cornered?: boolean
  selected?: boolean
  variant?: ItemVariants
  background?: 'default' | 'transparent'
  fontWeight?: 'normal' | 'medium' | 'bold'
  itemId: string
  // Real destination for the row. Given one, the row becomes a genuine link so
  // cmd/middle/right-click open it in a new tab; without one the row keeps
  // behaving as a click-handled `href="#"` placeholder.
  href?: string
  defaultCursor?: boolean
  extraClasses?: string[]
  iconExtraClasses?: string[]
  contentExtraClasses?: string[]
}

type ClickEvent = {
  itemId: string
  side?: 'left' | 'right'
}

const props = withDefaults(defineProps<Props>(), {
  variant: itemVariant.xlg,
  background: 'default',
  fontWeight: 'normal',
  href: undefined,
  defaultCursor: false,
  extraClasses: () => [],
  iconExtraClasses: () => [],
  contentExtraClasses: () => [],
})

const emit =
  defineEmits<{
    'item-click': [payload: ClickEvent]
    // The browser is resolving `href` itself and this component does not
    // navigate. Named for the common case, but it also covers shift (new
    // window) and alt (download) -- anything `isNewTabClick` matches. Consumers
    // only need it for side effects they would otherwise run in `item-click`,
    // such as invalidating a cache.
    'item-new-tab': [payload: ClickEvent]
  }>()

const $attrs = useAttrs()

const isDefaultBackground = computed(() => props.background === 'default')

type VariantClasses = {
  padding: string
  minHeight: string
  labelWrapper: string
  iconMargin: string
}

const variantClasses = {
  [itemVariant.xlg]: {
    padding: 'tw-p-2',
    minHeight: '',
    labelWrapper: 'tw-block tw-py-1',
    iconMargin: 'tw-mr-1',
  },
  [itemVariant.lg]: {
    padding: 'tw-py-1.5 tw-px-2',
    minHeight: 'tw-min-h-[32px]',
    labelWrapper: 'tw-block tw-leading-5',
    iconMargin: 'tw-mr-1.5',
  },
  [itemVariant.md]: {
    padding: 'tw-py-1.5 tw-px-2',
    minHeight: 'tw-min-h-[28px]',
    labelWrapper: 'tw-block tw-leading-4 tw-text-[13px]',
    iconMargin: 'tw-mr-1.5',
  },
} as const satisfies Record<ItemVariants, VariantClasses>

const currentVariantClasses = computed(() => variantClasses[props.variant])

const labelWrapperClass = computed(
  () => currentVariantClasses.value.labelWrapper
)

const iconMarginClass = computed(() => currentVariantClasses.value.iconMargin)

const containerClasses = computed(() => {
  return [
    currentVariantClasses.value.padding,
    currentVariantClasses.value.minHeight,
    {
      'tw-rounded': props.cornered,
      'tw-border-0': props.cornered,
      '[&+&]:tw-mt-0.5': props.cornered,
      'tw-border-b': !props.cornered,
      'tw-border-sf-slate-lighter-1': !props.cornered,
      'last:tw-border-b-0': !props.cornered,
      'tw-bg-sf-snow-lighter': isDefaultBackground.value && !props.selected,
      'tw-bg-sf-primary-subtle':
        props.selected && props.background === 'default',
      'tw-text-sf-snow-lighter':
        props.selected && props.background === 'transparent',
      'tw-bg-sf-primary': props.selected && props.background === 'transparent',
      'hover:tw-bg-sf-primary-darker':
        props.selected && props.background === 'transparent',
      'hover:tw-bg-sf-slate-subtle-2': !props.selected,
      'tw-text-sf-slate': isDefaultBackground.value && !props.selected,
      'hover:tw-text-sf-slate-dark-1':
        isDefaultBackground.value && !props.selected,
      'tw-text-sf-slate-dark-2': isDefaultBackground.value && props.selected,
      'tw-text-sf-neutral-darker-1':
        !isDefaultBackground.value && !props.selected,
      'tw-bg-transparent':
        !props.selected && props.background === 'transparent',
      [`tw-font-${props.fontWeight}`]: !!props.fontWeight,
    },
    ...props.extraClasses,
  ]
})

const linkBaseClasses = computed(() => {
  return {
    'tw-flex tw-flex-row tw-items-center tw-justify-start': true,
    'tw-text-inherit tw-no-underline': true,
    'hover:tw-text-inherit hover:tw-no-underline': true,
    'focus:tw-text-inherit focus:tw-no-underline': true,
    'active:tw-text-inherit active:tw-no-underline': true,
    'tw-cursor-pointer': !props.defaultCursor,
    '!tw-cursor-default': props.defaultCursor,
  }
})

const linkClasses = computed(() => {
  return {
    ...linkBaseClasses.value,
    'tw-grow': true,
    'tw-min-w-0': true,
  }
})

const rightLinkClasses = computed(() => linkBaseClasses.value)

const modifiedAttrs = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { onClick, ...rest } = $attrs
  return rest
})

const handleClick = (event: MouseEvent) => {
  // A modified click landing on the row's own padding has no anchor under the
  // pointer, so there is nothing for the browser to open. Bail out rather than
  // navigating the current tab against the modifier.
  if (props.href && isNewTabClick(event)) return

  event.preventDefault()
  event.stopPropagation()
  emit('item-click', { itemId: props.itemId })
}

const handleLeftClick = (event: MouseEvent) => {
  // With a real href the browser already knows how to open a new tab. Only stop
  // the event so the wrapping <li> handler can't preventDefault it, and leave
  // the default action alone.
  if (props.href && isNewTabClick(event)) {
    event.stopPropagation()
    emit('item-new-tab', { itemId: props.itemId, side: 'left' })
    return
  }

  event.preventDefault()
  event.stopPropagation()
  emit('item-click', { itemId: props.itemId, side: 'left' })
}

// Middle clicks dispatch `auxclick`, never `click`, so the new-tab signal needs
// its own listener. Nothing to stop or prevent here: no ancestor listens for
// `auxclick`, so the browser's native new-tab handling already runs.
// Right-click -> "Open link in new tab" fires no click-family event at all and
// therefore cannot be signalled.
const handleLeftAuxClick = (event: MouseEvent) => {
  // Middle button only. A right-click may also surface here in some browsers,
  // and opening a context menu is not a new-tab open.
  if (props.href && event.button === 1) {
    emit('item-new-tab', { itemId: props.itemId, side: 'left' })
  }
}

const handleRightClick = () => {
  emit('item-click', { itemId: props.itemId, side: 'right' })
}
</script>
